import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faStar,
  faUsers,
  faCloud,
  faShield,
  faHeadset,
  faCrown
} from '@fortawesome/free-solid-svg-icons';
import PurchaseModal from '../components/PurchaseModal';
import DemoModal from '../components/DemoModal';

gsap.registerPlugin(ScrollTrigger);

const Resources = ({ onPurchaseClick }) => {
  const pageRef = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  // 价格套餐数据
  const pricingPlans = [
    {
      id: 'basic',
      name: '基础版',
      price: '¥2,999',
      period: '/年',
      description: '适合小型物流企业',
      popular: false,
      features: [
        '基础财务管理',
        '账单管理',
        '基础报表',
        '5个用户账户',
        '10GB存储空间',
        '邮件支持'
      ],
      limitations: [
        '高级分析功能',
        '自定义报表',
        'API接口'
      ]
    },
    {
      id: 'standard',
      name: '标准版',
      price: '¥5,999',
      period: '/年',
      description: '适合中型物流企业',
      popular: true,
      features: [
        '完整财务管理',
        '智能账单处理',
        '高级报表分析',
        '20个用户账户',
        '100GB存储空间',
        '电话+邮件支持',
        '数据备份',
        '移动端应用'
      ],
      limitations: [
        '定制开发服务'
      ]
    },
    {
      id: 'premium',
      name: '企业版',
      price: '¥12,999',
      period: '/年',
      description: '适合大型物流企业',
      popular: false,
      features: [
        '全功能财务管理',
        'AI智能分析',
        '自定义报表',
        '无限用户账户',
        '1TB存储空间',
        '7×24小时专属支持',
        '数据安全保障',
        'API接口',
        '定制化服务',
        '专属客户经理'
      ],
      limitations: []
    }
  ];

  // 服务特色
  const serviceFeatures = [
    {
      icon: faCloud,
      title: '云端部署',
      description: '安全可靠的云端服务，随时随地访问'
    },
    {
      icon: faShield,
      title: '数据安全',
      description: '银行级安全保障，多重加密保护'
    },
    {
      icon: faHeadset,
      title: '专业支持',
      description: '专业技术团队提供全方位服务支持'
    },
    {
      icon: faUsers,
      title: '团队协作',
      description: '支持多用户协作，提升团队工作效率'
    }
  ];

  // 页面入场动画
  useEffect(() => {
    gsap.fromTo(
      pageRef.current.children,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
        ease: 'power2.out'
      }
    );
  }, []);

  const renderPricingCard = (plan) => {
    const isPopular = plan.popular;
    
    return (
      <div className="relative">
        {/* 推荐标签 */}
        {isPopular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center shadow-lg">
              <FontAwesomeIcon icon={faCrown} className="mr-2 text-yellow-300" />
              推荐
            </div>
          </div>
        )}
        
        {/* 卡片主体 */}
        <div className={`
          relative bg-white rounded-2xl shadow-xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-2xl
          ${isPopular ? 'border-blue-500 pt-12' : 'border-gray-200 pt-8'}
          ${isPopular ? 'bg-gradient-to-b from-blue-50 to-white' : ''}
        `}>
          
          {/* 套餐头部 */}
          <div className="text-center px-8 pb-6">
            <h3 className={`text-2xl font-bold mb-2 ${
              isPopular ? 'text-blue-900' : 'text-gray-900'
            }`}>
              {plan.name}
            </h3>
            <p className="text-gray-600 mb-6">{plan.description}</p>
            
            {/* 价格显示 */}
            <div className="mb-6">
              <div className="flex items-baseline justify-center">
                <span className={`text-5xl font-bold ${
                  isPopular ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {plan.price}
                </span>
                <span className="text-gray-500 ml-2 text-lg">{plan.period}</span>
              </div>
            </div>
          </div>
          
          {/* 功能列表 */}
          <div className="px-8 pb-8">
            <div className="space-y-4 mb-8">
              {plan.features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 mr-3">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xs" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{feature}</span>
                </div>
              ))}
              
              {plan.limitations.map((limitation, index) => (
                <div key={index} className="flex items-start opacity-60">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center mt-0.5 mr-3">
                    <FontAwesomeIcon icon={faTimes} className="text-gray-400 text-xs" />
                  </div>
                  <span className="text-gray-500 line-through leading-relaxed">{limitation}</span>
                </div>
              ))}
            </div>
            
            {/* 选择按钮 */}
            <button 
              onClick={onPurchaseClick}
              className={`
                w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg
                ${isPopular 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
                }
              `}
            >
              选择 {plan.name}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
      <div ref={pageRef} className="min-h-screen bg-gray-50 page-transition">
        {/* 英雄区域 */}
        <section className="py-20 bg-gradient-hero text-white flex items-center min-h-[300px]">
          <div className="container mx-auto px-4 text-center">
            <p className="text-2xl md:text-3xl font-medium text-blue-100">
              为不同规模的企业提供灵活的云账户管理解决方案
            </p>
          </div>
        </section>

        {/* 价格套餐 */}
        <section className="pt-20 pb-10">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-16">
             <h2 className="text-3xl font-bold text-gray-900 mb-4">
               价格套餐
             </h2>
             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
               根据企业规模和需求，选择最适合的服务方案
             </p>
           </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {pricingPlans.map(plan => (
              <div key={plan.id}>
                {renderPricingCard(plan)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 服务特色 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              服务特色
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              专业的技术团队和完善的服务体系，为您提供可靠保障
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceFeatures.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={feature.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 常见问题 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              常见问题
            </h2>
            <p className="text-xl text-gray-600">
              解答您关心的问题
            </p>
          </div>
          
          <div className="space-y-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">可以随时升级或降级套餐吗？</h3>
              <p className="text-gray-600">是的，您可以随时根据业务需求调整套餐，升级立即生效，降级在下个计费周期生效。</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">数据安全如何保障？</h3>
              <p className="text-gray-600">我们采用银行级加密技术，数据存储在国内顶级云服务商，并提供定期备份服务。</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">是否提供免费试用？</h3>
              <p className="text-gray-600">我们提供14天免费试用期，您可以体验完整功能后再决定是否购买。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA区域 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">准备开始了吗？</h2>
          <p className="text-xl text-blue-100 mb-8">立即联系我们的销售团队，获取专属优惠和定制方案</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onPurchaseClick}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              立即购买
            </button>
            <button 
              onClick={() => setIsDemoModalOpen(true)}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              预约演示
            </button>
          </div>
        </div>
      </section>

      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
    </div>
  );
};

export default Resources;